/* Logo marquee.
 *
 * The track slides by -50% in one CSS keyframe, so it loops seamlessly only
 * while each half of the track is at least as wide as the strip. Liquid
 * cannot measure, so it renders two copies of the set, and seven press marks
 * at desktop size come to about 1,220px: on any wider screen the right edge
 * ran out and left a blank gap until the loop reset (Tim's review Loom,
 * 24 Sep).
 *
 * This measures one set, then rebuilds the track as two identical halves of
 * enough sets to cover the strip. The duration scales with the number of sets
 * in a half, so the logos travel at the same speed however many copies there
 * are. Re-measured on resize, whenever a logo changes size (an image loading
 * or swapped) and whenever the editor reloads the section.
 *
 * Liquid already renders enough sets to cover a 2560px screen
 * (data-half-sets), and that count is a floor: this only adds sets on wider
 * screens, never removes them. White still showed at the end of the loop on
 * a client's screen after 24 Sep (Fiorenza, 7 Oct), most likely a measure
 * taken before the logos had their size, which the old version never redid.
 */
(() => {
  const init = (root) => {
    const track = root.querySelector('.logo-marquee__track');
    if (!track) return;

    const originals = [...track.querySelectorAll('[data-marquee-original]')];
    if (!originals.length) return;

    const baseSpeed = parseFloat(getComputedStyle(root).getPropertyValue('--marquee-speed')) || 45;
    const floor = parseInt(track.dataset.halfSets, 10) || 1;

    const clone = (node) => {
      const copy = node.cloneNode(true);
      copy.removeAttribute('data-marquee-original');
      copy.removeAttribute('data-shopify-editor-block');
      copy.setAttribute('aria-hidden', 'true');
      copy.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
      copy.querySelectorAll('img').forEach((img) => img.setAttribute('alt', ''));
      return copy;
    };

    // Liquid's own sets are already in place: keep them unless more are needed.
    let sets = track.children.length === originals.length * floor * 2 ? floor : 0;

    const build = () => {
      const setWidth = originals.reduce((sum, el) => sum + el.getBoundingClientRect().width, 0);
      if (!setWidth) return;

      const perHalf = Math.max(floor, Math.ceil(root.clientWidth / setWidth));
      if (perHalf === sets) return;
      sets = perHalf;

      track.querySelectorAll(':scope > :not([data-marquee-original])').forEach((el) => el.remove());

      const fragment = document.createDocumentFragment();
      for (let i = 1; i < perHalf * 2; i += 1) {
        originals.forEach((el) => fragment.appendChild(clone(el)));
      }
      track.appendChild(fragment);
      track.style.animationDuration = `${baseSpeed * perHalf}s`;
    };

    build();

    let frame;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(build);
    });
    observer.observe(root);
    originals.forEach((el) => observer.observe(el));
  };

  const scan = (scope = document) => scope.querySelectorAll('[data-logo-marquee]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
